import React, { useEffect, useState } from "react";
import { Plus, Trash2, Wallet, CalendarDays, Tag, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { tk, bn, fmtDate, todayStr } from "../lib/format";
import { useSettings } from "../context/SettingsContext";
import { GlassPage, GlassHeader, Modal, Loader, Field } from "../components/kit";

type ExpData = { expenses: any[]; total: number; by_category: Record<string, number> };

export default function Expenses() {
  const { settings } = useSettings();
  const EXPENSE_CATEGORIES = settings.expense_categories;
  const [month, setMonth] = useState(() => todayStr().slice(0, 7));
  const [data, setData] = useState<ExpData | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => ({ category: EXPENSE_CATEGORIES[0], amount: "", date: todayStr(), note: "" }));
  const [saving, setSaving] = useState(false);

  const emptyForm = () => ({ category: EXPENSE_CATEGORIES[0], amount: "", date: todayStr(), note: "" });

  const load = () => api.get(`/expenses?month=${month}`).then((r) => setData(r.data));
  useEffect(() => {
    setData(null);
    load();
  }, [month]);

  const submit = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("সঠিক পরিমাণ দিন");
    setSaving(true);
    try {
      await api.post("/expenses", { category: form.category, amount: amt, date: form.date, note: form.note });
      toast.success("খরচ যোগ হয়েছে");
      setOpen(false);
      setForm(emptyForm());
      if (form.date.slice(0, 7) !== month) setMonth(form.date.slice(0, 7));
      else load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("এই খরচটি মুছে ফেলবেন?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("মুছে ফেলা হয়েছে");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cats = data ? Object.entries(data.by_category).sort((a, b) => b[1] - a[1]) : [];

  return (
    <GlassPage testid="expenses-page">
      <GlassHeader
        title="খরচ / হিসাব"
        subtitle="দোকানের দৈনিক খরচ লিপিবদ্ধ করুন"
        actions={
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="expense-month-filter"
              className="h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-slate-100 text-sm"
            />
            <button
              onClick={() => { setForm(emptyForm()); setOpen(true); }}
              data-testid="add-expense-button"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-400 shadow-lg shadow-emerald-500/25 active:scale-95 transition-all"
            >
              <Plus size={18} /> নতুন খরচ
            </button>
          </div>
        }
      />

      {!data ? (
        <Loader />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 md:p-5 fade-up" data-testid="expense-month-total">
              <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-rose-400/30 to-rose-600/10 blur-2xl" />
              <div className="relative flex items-start justify-between">
                <p className="text-sm font-medium text-slate-300">এই মাসের মোট খরচ</p>
                <span className="w-9 h-9 rounded-xl grid place-items-center bg-rose-500/20 text-rose-300"><Wallet size={18} /></span>
              </div>
              <p className="num mt-2 text-xl md:text-2xl font-bold text-rose-300">{tk(data.total)}</p>
              <p className="text-xs text-slate-400 mt-1">{bn(data.expenses.length)} টি এন্ট্রি</p>
            </div>
            {cats.slice(0, 3).map(([cat, amt]) => (
              <div key={cat} className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 md:p-5 fade-up">
                <div className="relative flex items-start justify-between">
                  <p className="text-sm font-medium text-slate-300">{cat}</p>
                  <span className="w-9 h-9 rounded-xl grid place-items-center bg-blue-500/20 text-blue-300"><Tag size={16} /></span>
                </div>
                <p className="num mt-2 text-xl md:text-2xl font-bold text-white">{tk(amt)}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden fade-up">
            <div className="px-5 py-4 border-b border-white/10">
              <h2 className="text-[16px] font-semibold text-white">খরচের তালিকা</h2>
            </div>
            {data.expenses.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm">
                <Receipt className="mx-auto mb-2 opacity-60" size={28} />
                এই মাসে কোনো খরচ নেই
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="expenses-table">
                  <thead>
                    <tr className="text-slate-400 text-left">
                      <th className="px-5 py-2.5 font-medium">তারিখ</th>
                      <th className="px-3 py-2.5 font-medium">খাত</th>
                      <th className="px-3 py-2.5 font-medium">নোট</th>
                      <th className="px-3 py-2.5 font-medium text-right">পরিমাণ</th>
                      <th className="px-5 py-2.5 font-medium text-right">—</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenses.map((e) => (
                      <tr key={e.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`expense-row-${e.id}`}>
                        <td className="px-5 py-3 num text-slate-300 whitespace-nowrap">{fmtDate(e.date)}</td>
                        <td className="px-3 py-3">
                          <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-200">{e.category}</span>
                        </td>
                        <td className="px-3 py-3 text-slate-300">{e.note || "—"}</td>
                        <td className="px-3 py-3 text-right num font-bold text-rose-300">{tk(e.amount)}</td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => remove(e.id)} data-testid={`delete-expense-${e.id}`} className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:bg-rose-500/20 hover:text-rose-300 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="নতুন খরচ যোগ" testid="expense-modal">
        <div className="space-y-4">
          <Field label="খরচের খাত">
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="expense-category-select"
              className="w-full h-11 px-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm"
            >
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="পরিমাণ (৳)">
              <input
                type="number"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="expense-amount-input"
                placeholder="0"
                className="w-full h-11 px-3 rounded-lg border border-slate-300 text-slate-900 text-sm num"
              />
            </Field>
            <Field label="তারিখ">
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="expense-date-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-300 text-slate-900 text-sm"
              />
            </Field>
          </div>
          <Field label="নোট (ঐচ্ছিক)">
            <input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="expense-note-input"
              placeholder="বিস্তারিত..."
              className="w-full h-11 px-3 rounded-lg border border-slate-300 text-slate-900 text-sm"
            />
          </Field>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="save-expense-button"
            className="w-full h-12 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-500 disabled:opacity-60 transition-colors"
          >
            {saving ? "সংরক্ষণ হচ্ছে..." : "খরচ সংরক্ষণ করুন"}
          </button>
        </div>
      </Modal>
    </GlassPage>
  );
}
